import { Sun, Moon, Search } from "lucide-react";
import { motion } from "framer-motion";
import { useTheme } from "@/contexts/ThemeContext";
import { useLanguage } from "@/contexts/LanguageContext";

export default function ToggleArea({ onOpenSearch, compact = false }) {
  const { theme, toggle } = useTheme();
  const { lang, setLang, t } = useLanguage();
  const isDark = theme === "dark";

  return (
    <div className="flex items-center gap-3" data-testid="toggle-area">
      {/* Search */}
      <button
        onClick={onOpenSearch}
        aria-label={t("nav.search")}
        data-testid="nav-search-btn"
        className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
          isDark
            ? "text-[#9CA3AF] hover:text-white hover:bg-white/[0.06]"
            : "text-[#475569] hover:text-[#0F172A] hover:bg-black/[0.05]"
        }`}
      >
        <Search size={15} strokeWidth={1.75} />
      </button>

      {/* Language pill (EN / HU) */}
      <div
        className={`relative inline-flex items-center rounded-full p-0.5 border ${
          isDark ? "bg-white/[0.04] border-white/[0.08]" : "bg-black/[0.03] border-black/[0.08]"
        }`}
        data-testid="lang-pill"
      >
        <motion.span
          layoutId="lang-thumb"
          className={`absolute top-0.5 bottom-0.5 w-[34px] rounded-full ${
            isDark ? "bg-white/[0.10]" : "bg-white"
          }`}
          style={{ boxShadow: isDark ? "none" : "0 1px 3px rgba(15,23,42,0.08)" }}
          transition={{ type: "spring", stiffness: 400, damping: 32 }}
          animate={{ left: lang === "en" ? 2 : 36 }}
        />
        {[
          { code: "en", label: "EN" },
          { code: "hu", label: "HU" },
        ].map((l) => {
          const isActive = lang === l.code;
          let colorClass;
          if (isActive) {
            colorClass = isDark ? "text-white" : "text-[#0F172A]";
          } else if (isDark) {
            colorClass = "text-[#6B7280] hover:text-[#9CA3AF]";
          } else {
            colorClass = "text-[#94A3B8] hover:text-[#475569]";
          }
          return (
            <button
              key={l.code}
              onClick={() => setLang(l.code)}
              data-testid={`lang-btn-${l.code}`}
              aria-pressed={isActive}
              className={`relative z-10 w-[34px] h-7 text-[11px] font-mono font-medium tracking-wider transition-colors ${colorClass}`}
            >
              {l.label}
            </button>
          );
        })}
      </div>

      {/* Theme switch (elegant pill toggle) */}
      <button
        onClick={toggle}
        aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
        data-testid="theme-toggle-btn"
        className={`relative inline-flex items-center w-[58px] h-7 rounded-full p-0.5 transition-colors border ${
          isDark
            ? "bg-white/[0.04] border-white/[0.08]"
            : "bg-black/[0.03] border-black/[0.08]"
        }`}
      >
        <motion.span
          className={`absolute top-0.5 w-6 h-6 rounded-full flex items-center justify-center ${
            isDark ? "bg-[#1F2937]" : "bg-white"
          }`}
          style={{ boxShadow: isDark ? "0 0 0 1px rgba(255,255,255,0.10)" : "0 1px 3px rgba(15,23,42,0.12)" }}
          animate={{ left: isDark ? 28 : 2 }}
          transition={{ type: "spring", stiffness: 380, damping: 30 }}
        >
          {isDark ? (
            <Moon size={12} className="text-[#9CA3AF]" />
          ) : (
            <Sun size={12} className="text-[#EE1D25]" />
          )}
        </motion.span>
        {/* Soft tracks: sun on left, moon on right */}
        <Sun size={11} className={`ml-1 ${isDark ? "text-[#374151]" : "text-transparent"}`} />
        <Moon size={11} className={`ml-auto mr-1 ${isDark ? "text-transparent" : "text-[#CBD5E1]"}`} />
      </button>
    </div>
  );
}
